import { CreateFamilyMemberInput, UpdateFamilyMemberInput } from '@/types/family-member';

/**
 * Validation result with optional error message
 */
export type ValidationResult = {
  valid: boolean;
  error?: string;
};

/**
 * Validation rules for family member data
 */
export class FamilyMemberValidator {
  private static readonly MAX_NAME_LENGTH = 50;
  private static readonly MAX_NOTES_LENGTH = 1000;

  /**
   * Validate name field
   */
  static validateName(name: string): ValidationResult {
    if (typeof name !== 'string') {
      return { valid: false, error: 'Name must be a string' };
    }

    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return { valid: false, error: 'Name cannot be empty' };
    }

    if (trimmed.length > this.MAX_NAME_LENGTH) {
      return { valid: false, error: `Name cannot exceed ${this.MAX_NAME_LENGTH} characters` };
    }

    return { valid: true };
  }

  /**
   * Validate color field - accepts any valid 7-char hex color
   */
  static validateColor(color: string): ValidationResult {
    if (typeof color !== 'string') {
      return { valid: false, error: 'Color must be a string' };
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return { valid: false, error: 'Color must be a valid hex color (e.g. #FF5733)' };
    }

    return { valid: true };
  }

  /**
   * Validate notes field
   */
  static validateNotes(notes: string[] | null | undefined): ValidationResult {
    if (notes === null || notes === undefined) {
      return { valid: true };
    }

    if (!Array.isArray(notes)) {
      return { valid: false, error: 'Notes must be an array of strings' };
    }

    if (notes.some(note => typeof note !== 'string')) {
      return { valid: false, error: 'Each note must be a string' };
    }

    const totalLength = notes.reduce((sum, note) => sum + note.length, 0);
    if (totalLength > this.MAX_NOTES_LENGTH) {
      return { valid: false, error: `Notes cannot exceed ${this.MAX_NOTES_LENGTH} characters total` };
    }

    return { valid: true };
  }

  /**
   * Validate date of birth field
   */
  static validateDateOfBirth(dob: string | null | undefined): ValidationResult {
    if (dob === null || dob === undefined) {
      return { valid: true };
    }

    if (typeof dob !== 'string') {
      return { valid: false, error: 'Date of birth must be a string' };
    }

    // Validate ISO date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dob)) {
      return { valid: false, error: 'Date of birth must be in YYYY-MM-DD format' };
    }

    // Validate actual date
    const date = new Date(dob);
    if (isNaN(date.getTime())) {
      return { valid: false, error: 'Invalid date of birth' };
    }

    return { valid: true };
  }

  /**
   * Validate create input
   */
  static validateCreateInput(input: CreateFamilyMemberInput): ValidationResult {
    const nameResult = this.validateName(input.name);
    if (!nameResult.valid) return nameResult;

    const colorResult = this.validateColor(input.color);
    if (!colorResult.valid) return colorResult;

    const notesResult = this.validateNotes(input.notes);
    if (!notesResult.valid) return notesResult;

    const dobResult = this.validateDateOfBirth(input.dateOfBirth);
    if (!dobResult.valid) return dobResult;

    return { valid: true };
  }

  /**
   * Validate update input
   */
  static validateUpdateInput(input: UpdateFamilyMemberInput): ValidationResult {
    if (input.name !== undefined) {
      const nameResult = this.validateName(input.name);
      if (!nameResult.valid) return nameResult;
    }

    if (input.color !== undefined) {
      const colorResult = this.validateColor(input.color);
      if (!colorResult.valid) return colorResult;
    }

    const notesResult = this.validateNotes(input.notes);
    if (!notesResult.valid) return notesResult;

    const dobResult = this.validateDateOfBirth(input.dateOfBirth);
    if (!dobResult.valid) return dobResult;

    return { valid: true };
  }
}
